// SVG overlay that draws right-angle dependency arrows between vis-timeline
// items. Lives outside vis-timeline (which has no native arrow primitive),
// repositions on every rangechanged + scroll + resize event.
//
// Math: for each (depends_on -> task) pair we find both items' rendered
// bounding rects via the `.vis-item[data-id=...]` selector vis-timeline puts
// on every item DOM node. The arrow goes:
//   from = right edge of depends_on, vertical center
//   to   = left edge of task, vertical center
// Right-angle path: forward 12px, down to target y, then forward to target x.
//
// We don't try to handle every edge case (overlapping arrows, off-screen
// endpoints, etc.) — Phase 1.4 just needs a clean visual.

import { useEffect, useRef } from "preact/hooks";
import type { TaskDependency } from "@juuplan/shared";

interface Props {
  /** The vis-timeline container DIV (parent that holds .vis-item nodes). */
  container: HTMLElement | null;
  /** Tick value — bumped by GanttView on rangechanged / drag / resize. */
  tick: number;
  dependencies: TaskDependency[];
}

// "currentColor" lets the SVG inherit color via the .juuplan-dep-arrows
// CSS rule, which uses var(--color-outline). One source of truth and the
// stroke adapts to light/dark/night themes for free.
const STROKE = "currentColor";
const STROKE_WIDTH = 1;
const CORNER_RADIUS = 6;
const STEP_OUT = 14;
const ARROW_HEAD_ID = "juuplan-arrow-head";

interface Point {
  x: number;
  y: number;
}

interface RectPair {
  fromRight: Point;
  toLeft: Point;
}

function getItemRect(
  container: HTMLElement,
  id: number
): { left: number; right: number; top: number; bottom: number } | null {
  const el = container.querySelector<HTMLElement>(
    `.vis-item[data-id="${id}"]`
  );
  if (!el) return null;
  const cr = container.getBoundingClientRect();
  const r = el.getBoundingClientRect();
  // Coordinates relative to the SVG overlay (= container).
  return {
    left: r.left - cr.left,
    right: r.right - cr.left,
    top: r.top - cr.top,
    bottom: r.bottom - cr.top,
  };
}

function buildPair(
  container: HTMLElement,
  fromId: number,
  toId: number
): RectPair | null {
  const from = getItemRect(container, fromId);
  const to = getItemRect(container, toId);
  if (!from || !to) return null;
  return {
    fromRight: { x: from.right, y: (from.top + from.bottom) / 2 },
    toLeft: { x: to.left, y: (to.top + to.bottom) / 2 },
  };
}

function pathFor(pair: RectPair): string {
  const { fromRight: a, toLeft: b } = pair;
  const dx = b.x - a.x;
  // Pull the elbow out a constant distance unless the items are very close,
  // in which case use half the available width.
  const stepOut = dx > STEP_OUT * 2 ? STEP_OUT : Math.max(4, dx / 2);
  const midX = a.x + stepOut;
  const dy = b.y - a.y;

  // Tiny offsets: target on (almost) the same horizontal — straight line.
  if (Math.abs(dy) < 1) {
    return `M ${a.x} ${a.y} L ${b.x} ${b.y}`;
  }

  // Clamp the corner radius so it never overshoots either leg of the elbow.
  const maxCorner = Math.min(stepOut, Math.abs(dy) / 2, Math.abs(b.x - midX) / 2);
  const r = Math.max(0, Math.min(CORNER_RADIUS, maxCorner));

  const vSign = dy > 0 ? 1 : -1;
  const hSign = b.x > midX ? 1 : -1;

  // Two rounded elbows: (midX, a.y) and (midX, b.y).
  // Outgoing horizontal, drop/rise to b.y, then horizontal to b.x.
  return [
    `M ${a.x} ${a.y}`,
    `L ${midX - r} ${a.y}`,
    `Q ${midX} ${a.y} ${midX} ${a.y + r * vSign}`,
    `L ${midX} ${b.y - r * vSign}`,
    `Q ${midX} ${b.y} ${midX + r * hSign} ${b.y}`,
    `L ${b.x} ${b.y}`,
  ].join(" ");
}

export function DependencyArrows(props: Props): preact.JSX.Element | null {
  const { container, tick, dependencies } = props;
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!container || !svgRef.current) return;
    const svg = svgRef.current;
    const cr = container.getBoundingClientRect();
    svg.setAttribute("width", String(cr.width));
    svg.setAttribute("height", String(cr.height));
    svg.style.width = `${cr.width}px`;
    svg.style.height = `${cr.height}px`;
  }, [container, tick, dependencies]);

  if (!container) return null;

  const paths = dependencies
    .map((dep) => {
      const pair = buildPair(container, dep.depends_on_id, dep.task_id);
      if (!pair) return null;
      return { key: `${dep.depends_on_id}-${dep.task_id}`, d: pathFor(pair) };
    })
    .filter((x): x is { key: string; d: string } => x !== null);

  return (
    <svg
      ref={svgRef}
      class="juuplan-dep-arrows pointer-events-none absolute left-0 top-0"
      style={{ overflow: "hidden" }}
      aria-hidden="true"
    >
      <defs>
        <marker
          id={ARROW_HEAD_ID}
          viewBox="0 0 10 10"
          refX="8"
          refY="5"
          markerWidth="6"
          markerHeight="6"
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill={STROKE} />
        </marker>
      </defs>
      {paths.map((p) => (
        <path
          key={p.key}
          d={p.d}
          fill="none"
          stroke={STROKE}
          stroke-width={STROKE_WIDTH}
          stroke-linecap="round"
          stroke-linejoin="round"
          marker-end={`url(#${ARROW_HEAD_ID})`}
        />
      ))}
    </svg>
  );
}
